import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Eyebrow, FadeUp, MaskLine } from "@/components/Reveal";
import { RESIDENTS, TEAM } from "@/data/content";

const FACTS = [
  { value: "11.05.2024", label: "Premier événement & naissance de l'association" },
  { value: "03", label: "Artistes résident·es" },
  { value: "05", label: "Membres de l'association" },
  { value: "LYO", label: "Ancrage — scène underground lyonnaise" },
];

function Hero() {
  const { scrollY } = useScroll();
  const photoY = useTransform(scrollY, [0, 900], [0, 110]);

  const goEquipe = () => {
    if (window.__lenis) {
      window.__lenis.scrollTo("#equipe", { offset: -70 });
    } else {
      document.getElementById("equipe")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative overflow-hidden pt-28 sm:pt-32" aria-label="Introduction">
      <div
        className="pointer-events-none absolute -right-52 top-1/4 h-[560px] w-[560px] rounded-full bg-aubergine/60 blur-[130px]"
        aria-hidden="true"
      />
      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 pb-16 sm:px-8 lg:grid-cols-12 lg:gap-6 lg:pb-24">
        <div className="flex flex-col justify-center lg:col-span-7">
          <FadeUp>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-ice">
              Plateforme culturelle &amp; médiatique — Lyon, FR
            </p>
          </FadeUp>

          <FadeUp delay={0.1} className="mt-6">
            <img
              src="/assets/logo-white.png"
              alt="chroma."
              className="w-[min(64vw,340px)]"
              width="340"
              height="84"
              fetchPriority="high"
            />
          </FadeUp>

          <h1 className="sr-only">
            Chroma — plateforme culturelle et médiatique dédiée aux cultures latino-américaines dans
            la musique électronique et la scène underground à Lyon
          </h1>

          <div
            aria-hidden="true"
            className="mt-6 font-clash font-semibold uppercase leading-[0.92] tracking-tight"
          >
            <MaskLine delay={0.25}>
              <span className="block text-[clamp(2.4rem,7.5vw,6.5rem)]">Le club en</span>
            </MaskLine>
            <MaskLine delay={0.37}>
              <span className="text-outline-ice block text-[clamp(2.4rem,7.5vw,6.5rem)]">
                couleurs
              </span>
            </MaskLine>
            <MaskLine delay={0.49}>
              <span className="block text-[clamp(2.4rem,7.5vw,6.5rem)]">
                latines<span className="text-glow">.</span>
              </span>
            </MaskLine>
          </div>

          <FadeUp delay={0.65} className="mt-6 max-w-md">
            <p className="text-base leading-relaxed text-muted sm:text-lg">
              Musiques électroniques, cultures latino-américaines et scène underground — une
              plateforme qui informe, sélectionne et fait danser, depuis Lyon.
            </p>
          </FadeUp>

          <FadeUp delay={0.8} className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/notre-mission"
              data-testid="hero-cta-mission"
              className="group inline-flex items-center gap-2 bg-glow px-7 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-night transition-[background-color,transform] duration-300 hover:bg-fog active:scale-95"
            >
              Notre mission
              <ArrowUpRight
                size={15}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </Link>
            <button
              type="button"
              onClick={goEquipe}
              data-testid="hero-cta-equipe"
              className="group inline-flex items-center gap-2 border border-ice/50 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ice transition-[background-color,border-color] duration-300 hover:border-ice hover:bg-ice/10"
            >
              L'équipe
              <ArrowDown
                size={15}
                className="transition-transform duration-300 group-hover:translate-y-0.5"
              />
            </button>
          </FadeUp>
        </div>

        <div className="relative lg:col-span-5">
          <motion.figure
            style={{ y: photoY }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:-rotate-2"
          >
            <div className="border border-line bg-night p-2 shadow-[16px_16px_0_0_#0E2B2A]">
              <img
                src="/assets/crowd-pink.jpg"
                alt="Foule sous des lumières roses et violettes lors d'un événement Chroma"
                className="aspect-[3/4] w-full object-cover lg:aspect-auto lg:h-[560px]"
                width="1080"
                height="720"
              />
            </div>
            <figcaption className="absolute -bottom-4 left-6 border border-line bg-night px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted">
              Sur le dancefloor — Chroma, Lyon
            </figcaption>
            <span
              className="absolute -left-4 top-10 rotate-[8deg] bg-glow px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-night"
              aria-hidden="true"
            >
              Depuis 2024
            </span>
          </motion.figure>
        </div>
      </div>

      <p
        className="pointer-events-none absolute bottom-5 left-5 hidden font-mono text-[10px] uppercase tracking-[0.35em] text-muted lg:block"
        aria-hidden="true"
      >
        Scroll ↓
      </p>
    </section>
  );
}

function Intro() {
  return (
    <section className="relative border-t border-line" aria-label="Qui est Chroma">
      <span className="absolute left-4 top-4 font-mono text-ice/40" aria-hidden="true">+</span>
      <span className="absolute right-4 top-4 font-mono text-ice/40" aria-hidden="true">+</span>

      <div className="mx-auto grid max-w-[1400px] gap-10 px-5 py-24 sm:px-8 md:py-32 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <Eyebrow index="01">Qui est Chroma</Eyebrow>
          <h2 className="mt-8 font-clash text-3xl font-medium leading-[1.12] tracking-tight sm:text-5xl">
            <MaskLine>
              <span>
                Informer, partager et mettre en lumière les cultures{" "}
                <span className="text-ice">latino-américaines</span>
              </span>
            </MaskLine>
            <MaskLine delay={0.1}>
              <span>
                dans la musique électronique et la scène{" "}
                <span className="text-glow">underground</span> en France.
              </span>
            </MaskLine>
          </h2>
        </div>
        <div className="flex flex-col justify-end lg:col-span-4">
          <FadeUp delay={0.2}>
            <p className="text-base leading-relaxed text-muted">
              Curateur et organisateur d'événements, Chroma tisse des ponts entre les publics, les
              artistes et les histoires — avec une vision vibrante et engagée de la culture latine.
            </p>
            <Link
              to="/notre-mission"
              data-testid="intro-cta-mission"
              className="link-underline mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-glow"
            >
              Lire notre mission
              <ArrowUpRight size={15} />
            </Link>
          </FadeUp>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-2 gap-px border border-line bg-line lg:grid-cols-4">
          {FACTS.map((fact, i) => (
            <FadeUp key={fact.value} delay={i * 0.07} className="bg-night p-6 sm:p-8">
              <p className="font-clash text-2xl font-semibold tracking-tight text-fog sm:text-4xl">
                {fact.value}
              </p>
              <p className="mt-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-muted">
                {fact.label}
              </p>
            </FadeUp>
          ))}
        </div>
      </div>

      <FadeUp className="mt-20">
        <figure className="relative">
          <img
            src="/assets/crowd-beams.jpg"
            alt="Foule en concert sous des faisceaux de lumière — événement Chroma × Casa Under × Industrial Girls, Soundfactory"
            className="aspect-[16/10] w-full object-cover sm:aspect-[21/9]"
            loading="lazy"
            width="1080"
            height="720"
          />
          <figcaption className="absolute bottom-0 left-0 bg-night/85 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted backdrop-blur-sm">
            Chroma × Casa Under × Industrial Girls — Soundfactory
          </figcaption>
        </figure>
      </FadeUp>
    </section>
  );
}

function TeamGrid() {
  const spans = [
    "lg:col-span-4",
    "lg:col-span-4 lg:mt-14",
    "lg:col-span-4",
    "lg:col-span-4 lg:col-start-3 lg:mt-6",
    "lg:col-span-4 lg:col-start-7 lg:mt-20",
  ];
  return (
    <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-12">
      {TEAM.map((member, i) => (
        <FadeUp
          key={member.slug}
          delay={i * 0.06}
          className={spans[i]}
          data-testid={`team-member-${member.slug}`}
        >
          <figure className="group">
            <div className="relative overflow-hidden border border-line">
              <img
                src={member.photo}
                alt={member.alt}
                className="aspect-[3/4] w-full object-cover saturate-[0.55] transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.04] group-hover:saturate-100"
                loading="lazy"
              />
              <span className="absolute left-3 top-3 bg-night/80 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-fog/90">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <figcaption className="mt-4">
              <p className="font-clash text-lg font-medium uppercase tracking-tight sm:text-xl">
                {member.name}
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
                {member.role}
              </p>
            </figcaption>
          </figure>
        </FadeUp>
      ))}
    </div>
  );
}

function ResidentBlock({ artist, flip }) {
  return (
    <article
      data-testid={`resident-${artist.slug}`}
      className="grid items-start gap-10 lg:grid-cols-12 lg:gap-6"
    >
      <FadeUp
        className={`relative lg:col-span-5 ${flip ? "lg:order-2 lg:col-start-8" : ""}`}
      >
        <figure className={`relative ${flip ? "lg:rotate-[1.5deg]" : "lg:-rotate-[1.5deg]"}`}>
          <div className="border border-line bg-night p-2 shadow-[14px_14px_0_0_#24003F]">
            <img
              src={artist.photo}
              alt={artist.alt}
              className={`w-full object-cover ${
                artist.slug === "kunawara"
                  ? "aspect-[4/3]"
                  : "aspect-[3/4] max-h-[600px]"
              }`}
              loading="lazy"
            />
          </div>
          <figcaption className="absolute -bottom-4 right-4 border border-line bg-night px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted">
            {artist.origin}
          </figcaption>
        </figure>
      </FadeUp>

      <div className={`lg:col-span-7 ${flip ? "lg:order-1 lg:col-start-1" : "lg:col-start-6"}`}>
        <p className="bg-word pointer-events-none select-none font-clash text-7xl font-bold uppercase leading-none sm:text-8xl" aria-hidden="true">
          {artist.index}
        </p>
        <MaskLine className="-mt-8 sm:-mt-10">
          <span className="block font-clash text-5xl font-semibold uppercase tracking-tight sm:text-6xl">
            {artist.name}
          </span>
        </MaskLine>
        <FadeUp delay={0.1} className="mt-5 flex flex-wrap gap-2">
          {artist.genres.map((genre) => (
            <span
              key={genre}
              className="border border-ice/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ice"
            >
              {genre}
            </span>
          ))}
        </FadeUp>
        {artist.bio.map((paragraph, j) => (
          <FadeUp key={j} delay={0.15 + j * 0.08} className="mt-5 max-w-2xl">
            <p className="text-base leading-relaxed text-muted">{paragraph}</p>
          </FadeUp>
        ))}
        <FadeUp delay={0.3} className="mt-7 flex flex-wrap gap-x-8 gap-y-3">
          <a
            href={artist.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`resident-instagram-${artist.slug}`}
            className="link-underline inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:text-glow"
          >
            Instagram
            <ArrowUpRight size={14} />
          </a>
          {artist.sets.map((set) => (
            <a
              key={set.url}
              href={set.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`resident-set-${artist.slug}-${set.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="link-underline inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-ice transition-colors hover:text-glow"
            >
              {set.label}
              <ArrowUpRight size={14} />
            </a>
          ))}
        </FadeUp>
      </div>
    </article>
  );
}

function Equipe() {
  return (
    <section id="equipe" className="relative border-t border-line" aria-label="L'équipe">
      <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 md:py-32">
        <Eyebrow index="02">L'équipe</Eyebrow>
        <h2 className="mt-10 max-w-4xl font-clash text-4xl font-semibold uppercase leading-[0.95] tracking-tight sm:text-6xl">
          <MaskLine>
            <span>
              L'association <span className="text-glow">&amp;</span>
            </span>
          </MaskLine>
          <MaskLine delay={0.1}>
            <span>
              ses <span className="text-outline-fog">résident·es</span>
            </span>
          </MaskLine>
        </h2>
        <FadeUp delay={0.2} className="mt-6 max-w-xl">
          <p className="text-base leading-relaxed text-muted">
            Cinq membres font tourner l'association — et trois artistes résident·es incarnent le son
            de Chroma derrière les platines.
          </p>
        </FadeUp>

        <TeamGrid />

        <div className="mt-28 space-y-28 md:mt-36 md:space-y-36">
          {RESIDENTS.map((artist, i) => (
            <ResidentBlock key={artist.slug} artist={artist} flip={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Cta() {
  return (
    <section className="border-t border-line bg-aubergine" aria-label="Contact">
      <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 md:py-32">
        <FadeUp>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-ice">
            Une date ? Un lieu ? Une idée ?
          </p>
        </FadeUp>
        <MaskLine className="mt-6" delay={0.1}>
          <Link
            to="/contact"
            data-testid="cta-contact-link"
            className="group inline-flex flex-wrap items-baseline gap-x-5 font-clash text-[clamp(2.6rem,8vw,7rem)] font-semibold uppercase leading-[0.95] tracking-tight"
          >
            <span className="border-b-[0.07em] border-fog/30 transition-colors duration-500 group-hover:border-glow group-hover:text-glow">
              Écrire à Chroma
            </span>
            <ArrowUpRight
              className="h-[0.55em] w-[0.55em] transition-transform duration-500 group-hover:-translate-y-2 group-hover:translate-x-2 group-hover:text-glow"
              aria-hidden="true"
            />
          </Link>
        </MaskLine>
      </div>
    </section>
  );
}

export default function Accueil() {
  return (
    <div data-testid="page-accueil">
      <Hero />
      <Intro />
      <Marquee />
      <Equipe />
      <Cta />
    </div>
  );
}
